/**
 * httpStorage — StoragePort adapter backed by the real Commercial Operator
 * backend (FastAPI + persistent repository — see backend/app/db.py).
 *
 * Nunca llama al backend con una URL absoluta ni con un token: todas las
 * rutas son relativas (`/api/storage/...`). En desarrollo local, el proxy
 * de craco.config.js inyecta el bearer del backend server-side; en
 * producción, el backend sirve el build del frontend en el mismo origen y
 * la sesión se autentica vía cookie HttpOnly (`co_auth`, ver backend/app/
 * auth.py) que el navegador nunca lee ni conoce como texto plano
 * (TRUST_BOUNDARY.md).
 */

const BASE = "/api/storage";

function buildQuery(params) {
  const entries = Object.entries(params || {}).filter(
    ([, v]) => v !== undefined && v !== null && v !== ""
  );
  if (!entries.length) return "";
  return `?${new URLSearchParams(entries).toString()}`;
}

async function req(path, { method = "GET", body, params } = {}) {
  const res = await fetch(`${BASE}${path}${buildQuery(params)}`, {
    method,
    headers: body !== undefined ? { "Content-Type": "application/json" } : undefined,
    body: body !== undefined ? JSON.stringify(body) : undefined,
  });
  if (res.status === 404) return null;
  if (!res.ok) {
    const detail = await res.text().catch(() => "");
    throw new Error(`storage_http_error_${res.status}: ${detail.slice(0, 200)}`);
  }
  return res.json();
}

/** @returns {import('../../domain/ports').StoragePort} */
export function createHttpStorage() {
  return {
    // ---- Actors ----
    async listActors(filter = {}) {
      return (await req("/actors", { params: filter })) || [];
    },
    async getActor(id) {
      return req(`/actors/${id}`);
    },
    async saveActor(actor) {
      return req("/actors", { method: "POST", body: actor });
    },

    // ---- Signals ----
    async listSignals(actorId) {
      return (await req(`/actors/${actorId}/signals`)) || [];
    },
    async saveSignal(signal) {
      return req("/signals", { method: "POST", body: signal });
    },

    // ---- Opportunities ----
    async listOpportunities(filter = {}) {
      return (await req("/opportunities", { params: filter })) || [];
    },
    async getOpportunity(id) {
      return req(`/opportunities/${id}`);
    },
    async saveOpportunity(opp) {
      return req("/opportunities", { method: "POST", body: opp });
    },
    async updateOpportunity(id, patch) {
      const updated = await req(`/opportunities/${id}`, { method: "PATCH", body: patch });
      if (!updated) throw new Error("Opportunity not found");
      return updated;
    },

    // ---- Plays ----
    async listPlays(opportunityId) {
      return (await req(`/opportunities/${opportunityId}/plays`)) || [];
    },
    async getLatestPlay(opportunityId) {
      return req(`/opportunities/${opportunityId}/plays/latest`);
    },
    async savePlay(play) {
      return req("/plays", { method: "POST", body: play });
    },

    // ---- Outcomes ----
    async listOutcomes(opportunityId) {
      return (await req("/outcomes", { params: { opportunityId } })) || [];
    },
    async saveOutcome(outcome) {
      return req("/outcomes", { method: "POST", body: outcome });
    },

    // ---- Closures ----
    async listClosures(filter = {}) {
      return (await req("/closures", { params: filter })) || [];
    },
    async saveClosure(closure) {
      return req("/closures", { method: "POST", body: closure });
    },

    // ---- Team ----
    async listTeamMembers() {
      return (await req("/team")) || [];
    },
    async saveTeamMember(member) {
      return req("/team", { method: "POST", body: member });
    },

    // ---- Diagnostics (opcional -- no forma parte del contrato StoragePort
    // probado por container.conformance.test.js; solo el adapter http lo
    // implementa) ----
    // Hallazgo real (diagnóstico WhatsApp E2E, agosto 2026): ingestActors
    // corre enteramente en el navegador -- sin esto, un fallo real de motor
    // solo existía en la consola del navegador, invisible desde fuera.
    // Deliberadamente best-effort: si esto falla, nunca debe romper el
    // flujo de importación que lo llama (ver domain/portfolio.js).
    async reportClientError(event, detail) {
      try {
        await req("/diagnostics/client-error", { method: "POST", body: { event, detail } });
      } catch {
        /* diagnóstico best-effort -- nunca debe interrumpir al llamador */
      }
    },
  };
}
